import type { ToolDefinition, ToolType } from "../types";

const IMAGE_CROPPER_TOOL_TYPE = {
  label: "Free Online Cropper",
  icon: "Crop",
} as const satisfies ToolType;

const imageCropperTool: ToolDefinition = {
  slug: "image-cropper",
  // GSC PASS #2: Page already ranks p1 for "without losing quality" + "freehand" clusters
  // (working — do NOT weaken). The loss is on HEAD terms: "image cropper" (266 imp, pos 46),
  // "photo cropper" (98 imp, pos 60), "crop image online" (81 imp, pos 81), "free image cropper"
  // (77 imp, pos 48). Strategy: free up title/H1/first-sentence (highest-weighted slots) for head
  // terms; let proven quality/freehand stay carried by the body.
  // CHANGE: added "photo cropper" (was entirely absent from title despite 98 imp) + kept the
  // free/online/quality signals the page already converts on.
  name: "Free Image Cropper & Photo Cropper Online | No Quality Loss",
  // CHANGE: H1 was "Online Image Cropper Without Quality Loss" — that reinforced the quality-niche
  // pigeonholing. Rebalanced to head-term-led so Google reads this as a general cropper.
  title: "Free Image Cropper: Crop Photos & Images Online",
  // CHANGE: front-loads head terms "image cropper" (266 imp), "photo cropper" (98 imp),
  // "crop images online" (81 imp), plus a "pictures" nod ("crop picture online" 50 imp / "picture
  // cropper" 37 imp), while keeping the quality signal that already ranks.
  description:
    "Free online image cropper and photo cropper. Crop images and pictures online to exact pixels, aspect ratios, or freehand shapes — without losing quality. No watermark, no account.",
  // CHANGE: leads with the general value prop (free online image cropper + photo cropper + crop
  // images online) before the proven quality/freehand differentiators. 40–75 word rule respected.
  longDescription:
    "A free online image cropper and photo cropper that delivers clean crops to exact pixel coordinates, preset aspect ratios, or freehand boundaries — without losing quality. Upload a JPEG, PNG, WebP, or GIF and crop images online in seconds. No watermark, no account required.",
  toolType: {
    label: "Image Cutter",
    icon: "Crop",
  },
  heroBadges: [
    "100% Free",
    "No Watermark",
    "No Account Required",
    "Exact Pixel Dimensions",
    "Preserves Transparency",
    "Works on Mobile",
  ],
  category: "Editing Tools",
  categorySlug: "image-editing-tools",
  href: "/tools/image-cropper",
  api: "/api/v1/crop",
  // KEYWORDS: metakeywords list per rules.
  // CHANGE: removed "divide photo in equal parts" — verified tool spec crops ONE rectangle only;
  // dividing into parts is not a real feature, so it's off-scope and risks a relevance mismatch.
  keywords: [
    "snappy image cutter app",
    "whatsapp dp dimensions checker",
    "convert picture to square maker",
    "crop jpeg without losing quality",
    "facebook profile framing analyzer",
    "change webp aspect ratio online",
    "freehand png cropping software",
    "adjust passport photo size tool",
    "cut face out of picture",
  ],
  pageContent: {
    sections: {
      steps: {
        title: "How to Crop Images Online",
        description:
          "Follow these simple steps to cut out your picture and adjust its dimensions precisely — no software download needed.",
      },
      benefits: {
        title: "Why Use Our Free Image Cropper and Photo Cropper?",
        description:
          "Get exact, high-resolution crops without downloading bulky editing software or degrading your image quality.",
      },
      faqs: {
        title: "Frequently Asked Questions",
        description:
          "Common questions about cropping images online, pixel dimensions, aspect ratios, and whether cropping a photo reduces quality.",
      },
    },
    introBlocks: [
      {
        kind: "text",
        // Proven page-1 H2 for "image cropper without losing quality" (pos 8.57) — kept intact.
        title: "Image Cropper Without Losing Quality",
        level: "h2",
        paragraphs: [
          // CHANGE: first sentence now opens with the head terms ("free image cropper", "photo
          // cropper", "crop images online") — the most heavily weighted text on the page — before
          // pivoting to the quality angle. This is the core rebalance toward the buried head cluster.
          "Our free image cropper and photo cropper let you crop images online to any size, ratio, or shape in seconds — and cropping a photo online should never cost you sharpness or resolution. This image cropper processes JPEG, PNG, WebP, and GIF files by trimming only the exact pixel boundary you define — nothing inside the crop region is recompressed, resampled, or downgraded. Whether you need to crop image without losing quality online free or want a precise rectangular cut for a document photo, the result downloads in your original file format with its full fidelity intact.",
          // High-impression buried generics kept: "crop images online", "crop pictures online",
          // "online cropping tool", "free online photo cropper".
          "Whether you use this as a free online photo cropper for social media or a precise pixel-level image cropping tool for web development work, the workflow is the same: upload, define your boundaries, download. No account. No watermark. Works as a mobile-friendly online cropping tool on any device.",
        ],
        note: "This is a trimming operation only. No compression, no filters, no resampling — just a clean rectangular crop.",
      },
      {
        kind: "callouts",
        groups: [
          {
            title: "What You Can Crop",
            layout: "list",
            iconColor: "primary",
            items: [
              {
                // "square image cropper" (11 imp, pos 27) — near page 1.
                label: "Square Image Cropper",
                icon: "Square",
                description:
                  "Crop any photo to a perfect 1:1 square for Instagram grid posts, WhatsApp DPs, or Facebook profile pictures without losing the subject.",
                accent: "primary",
              },
              {
                label: "Official Document Photos",
                icon: "FileText",
                description:
                  "Crop photo to passport size free, meeting strict dimension requirements for visa and government ID applications including Nigerian and US passport standards.",
                accent: "primary",
              },
              {
                // "screenshot cropper" (11 imp, pos 32) — near page 1.
                label: "Screenshot Cropper",
                icon: "Monitor",
                description:
                  "Quickly trim a screenshot to remove toolbars, status bars, or excess whitespace before sharing or embedding in documentation — preserves PNG sharpness fully.",
                accent: "primary",
              },
              {
                label: "Web & Banner Images",
                icon: "MonitorPlay",
                description:
                  "Crop WebP and AVIF files to exact banner or thumbnail dimensions to improve website loading speed and Core Web Vitals LCP scores.",
                accent: "primary",
              },
            ],
          },
        ],
      },
      {
        kind: "text",
        // Pixel + aspect-ratio + freehand cluster H2. Pixel terms sit at pos 17–29 (close), so
        // this stays strong. "image cropper by pixel" = 31 imp.
        title:
          "Pixel Cropper, Aspect Ratio Cropper, and Freehand Crop — All in One",
        level: "h2",
        paragraphs: [
          "Use this tool as a full pixel cropper: enter exact left, top, right, and bottom coordinates to crop image by pixels and fit a precise HTML container, UI layout, or design specification. If you prefer ratio-based framing, switch to the aspect ratio cropper — choose 1:1 for a square profile picture, 16:9 for a YouTube thumbnail, or 4:5 for an Instagram portrait, and the crop window constrains itself automatically. You can also crop an image to a specific size by entering target width and height dimensions directly.",
          // Freehand cluster — page-1 winner (pos 9–16). Kept fully intact.
          "For non-rectangular needs, switch to freehand crop mode and draw your own boundary freely. This free hand image cropper mode is ideal for removing an irregular margin, trimming a scanned document, or isolating a specific region without being locked to a preset shape. PNG transparency is preserved throughout — crop a PNG online and the alpha channel comes through intact, with no black or white fill introduced.",
        ],
      },
      {
        kind: "table",
        tables: [
          {
            title: "Popular Social Media Crop Dimensions (2026 Guidelines)",
            description:
              "Use these pixel dimensions to guide your custom crops for maximum sharpness across social platforms.",
            columns: [
              "Platform & Use",
              "Aspect Ratio",
              "Recommended Pixel Size",
            ],
            rows: [
              {
                "Platform & Use": "WhatsApp DP / Profile Picture",
                "Aspect Ratio": "1:1 (Square)",
                "Recommended Pixel Size": "500 x 500 px or larger",
              },
              {
                "Platform & Use": "Instagram Grid Post",
                "Aspect Ratio": "4:5 (Portrait)",
                "Recommended Pixel Size": "1080 x 1350 px",
              },
              {
                "Platform & Use": "YouTube Thumbnail",
                "Aspect Ratio": "16:9 (Widescreen)",
                "Recommended Pixel Size": "1280 x 720 px",
              },
              {
                "Platform & Use": "Facebook Profile Picture",
                "Aspect Ratio": "1:1 (Square)",
                "Recommended Pixel Size": "1080 x 1080 px",
              },
              {
                "Platform & Use": "Standard Passport Photo (General)",
                "Aspect Ratio": "2:2.5 (Varies by country)",
                "Recommended Pixel Size": "600 x 750 px (US) / 35×45mm equiv.",
              },
              {
                "Platform & Use": "Nigerian Passport Photo",
                "Aspect Ratio": "3.5:4.5",
                "Recommended Pixel Size": "35mm × 45mm, white background",
              },
            ],
            note: "Always start with the highest resolution source image possible before cropping to avoid pixelation in the output.",
            emphasisColumns: ["Platform & Use"],
            monospaceColumns: ["Recommended Pixel Size"],
          },
        ],
      },
    ],
    steps: [
      {
        number: "01",
        title: "Upload Your Image",
        description:
          "Drag and drop or click to upload a JPEG, PNG, WebP, or GIF — including screenshots and high-resolution photos. Files up to 20MB are fully supported and processed securely.",
      },
      {
        number: "02",
        title: "Set Crop Boundaries, Aspect Ratio, or Freehand Region",
        description:
          "Enter exact pixel coordinates (left, top, right, bottom) for precise pixel-level cropping, select a preset aspect ratio like 1:1 or 16:9, draw a freehand crop boundary, or choose circle crop mode for round profile pictures.",
      },
      {
        number: "03",
        title: "Download Your Cropped Result",
        description:
          "Click Crop and download your result in its original format — JPEG, PNG, or WebP. PNG transparency is fully preserved. No watermark added. Your cropped image is ready to use immediately.",
      },
    ],
    benefits: [
      {
        icon: "Maximize",
        title: "Precise Pixel Cropper",
        description:
          "Enter exact left, top, right, and bottom pixel values to crop image with dimensions that match any design spec or HTML container. Works as a true pixel cropper for developers and designers who need zero guesswork.",
      },
      {
        icon: "Image",
        title: "Crop Without Losing Quality",
        description:
          "Our free photo cropper strictly trims the outer edges you define. It never recompresses, resamples, or downgrades the pixels that remain — your cropped image retains full original fidelity. No quality loss, no watermark.",
      },
      {
        icon: "Layers",
        title: "Preserves PNG Transparency",
        description:
          "Crop a PNG online or trim a WebP logo without destroying the alpha channel. Transparent backgrounds stay transparent — no black or white fill introduced regardless of the crop mode you use.",
      },
      {
        icon: "Crop",
        // CHANGE: description now also carries "online cropping tool", "online photo cropper" and
        // "crop pictures online" — buried head terms (online photo cropper 57 imp / pos 56).
        title: "Free Online Cropping Tool",
        description:
          "No software download. No account. No watermark. This free online cropping tool and online photo cropper runs entirely in your browser — crop pictures online from any device, including mobile phones.",
      },
      {
        icon: "PenLine",
        title: "Freehand Crop Mode",
        description:
          "Draw your own crop boundary freely for irregular trims, scanned documents, or any region that does not fit a standard rectangle or ratio. A true free hand image cropper built into the same tool.",
      },
    ],
    faqs: [
      {
        question:
          "Does this image cropper preserve quality without losing sharpness?",
        answer:
          "Yes — this is an image cropper without losing quality. It strictly removes the outer pixels you mark as unwanted and never recompresses or resamples the pixels that remain inside your crop boundary. The cropped file retains the same visual fidelity as the original source image in that region. The only scenario where output looks softer is if you crop deeply into a very low-resolution area — cropping cannot add pixels that were never there.",
      },
      {
        question: "Does cropping a photo reduce quality?",
        answer:
          "Not when done correctly. Cropping removes the outer pixels you do not want — it does not touch the pixels that remain. Our free photo cropper does not add compression or resample the image during the crop operation. If the result looks pixelated, it means the area you cropped into was already low resolution in the original file — cropping cannot add detail that was never captured.",
      },
      {
        question: "Can I crop by pixels using exact coordinate values?",
        answer:
          "Yes. This tool works as a full pixel cropper. Enter the exact left, top, right, and bottom pixel values to define your crop rectangle precisely. This is particularly useful for developers who need to crop an image by pixels to fit a specific HTML container, UI element, or design mockup. The crop boundary must stay within the image bounds, with right greater than left and bottom greater than top.",
      },
      {
        question: "How does the freehand crop mode work?",
        answer:
          "Switch to freehand mode and draw your crop boundary directly on the image. Unlike preset ratios or pixel coordinates, freehand crop online lets you define an irregular region — useful for trimming scanned documents, removing uneven borders, or isolating a specific area. The result is still delivered as a rectangular bounding box of your drawn region, returned in your source file format.",
      },
      {
        question: "How does the aspect ratio cropper work?",
        answer:
          "Select a preset aspect ratio — such as 1:1 for square, 4:5 for Instagram portrait, or 16:9 for YouTube thumbnails — and the tool automatically constrains your crop selection to that ratio. You can then reposition the crop window anywhere on the image. This makes it easy to crop pictures online to standard social media dimensions without manually calculating pixel values.",
      },
      {
        question: "How do I crop an image to a perfect square (1:1) online?",
        answer:
          "Select the 1:1 aspect ratio preset in the cropper to activate square crop mode. Reposition the square crop window over your subject, then click Crop. This square image cropper delivers a perfectly equal width and height output — ideal for WhatsApp DPs, Instagram profile pictures, and Facebook avatars. We recommend a minimum source size of 500×500 pixels for sharp results.",
      },
      {
        question: "Can I use this as a screenshot cropper online?",
        answer:
          "Yes — the tool works on screenshots exactly like any other image. Upload your PNG or JPEG screenshot, set the crop boundaries to remove toolbars, status bars, or unwanted edges, and download the trimmed result. Screenshots from phones or desktops are typically PNG files and this cropper preserves their transparency and sharpness completely.",
      },
      {
        // CHANGE: this slot previously held a THIRD near-duplicate "quality" FAQ. Repurposed into a
        // head-term/free FAQ to capture the biggest buried cluster: "free image cropper" (77 imp),
        // "photo cropper" (98 imp), "online photo cropper" (57 imp), "crop picture online" (50 imp),
        // "picture cropper" (37 imp). The "crop without losing quality online free" intent is still
        // fully covered by FAQ #1, FAQ #2, and the opening H2 body copy.
        question: "Is this image cropper and photo cropper free to use online?",
        answer:
          "Yes — this is a 100% free online image cropper and photo cropper with no account and no watermark. Upload any JPEG, PNG, WebP, or GIF and crop pictures online directly in your browser on desktop or mobile. There are no hidden limits and no software to install — just upload, set your crop, and download the result in your original format.",
      },
      {
        question:
          "What is the recommended aspect ratio for a WhatsApp DP in 2026?",
        answer:
          "A WhatsApp display picture is shown as a circle but cropped from a 1:1 square source. Use the square image cropper to set a 1:1 ratio, center your face or subject, and download. We recommend a minimum of 500×500 pixels for a sharp result. WhatsApp applies the circular mask automatically — you do not need to create the circle yourself.",
      },
      {
        question: "How to crop photos for Nigerian passport standards in 2026?",
        answer:
          "Nigerian passport photos require 35mm × 45mm with a plain white background and the full face clearly visible. Upload your photo, set a 3.5:4.5 aspect ratio, crop closely around the head and upper shoulders, and download as a high-quality JPEG. The output meets standard print and digital submission requirements for Nigerian passport and visa applications.",
      },
    ],
    useCases: [
      {
        label: "Social Media Users",
        icon: "MessageCircle",
        description:
          "Crop pictures online to exact profile dimensions for WhatsApp, Facebook, and Instagram — ensuring no important detail is cut off by a circular frame.",
        accent: "primary",
      },
      {
        label: "Web Developers",
        icon: "Code",
        description:
          "Use as a pixel cropper to trim logos and UI assets to exact coordinate specs that fit CSS containers perfectly without guessing.",
        accent: "primary",
      },
      {
        label: "Content Creators",
        icon: "Video",
        description:
          "Frame high-resolution photography into 16:9 widescreen for YouTube thumbnails or 4:5 portrait for Instagram — with exact aspect ratio control.",
        accent: "primary",
      },
    ],
    audiences: [
      {
        label: "Job Seekers",
        icon: "Briefcase",
        description:
          "Crop professional headshots to the exact pixel dimensions required for LinkedIn profiles, digital resumes, and online job applications.",
        accent: "accent",
      },
      {
        label: "Travelers & Expats",
        icon: "Globe",
        description:
          "Prepare digital passport and visa photos to exact governmental aspect ratios and pixel sizes from home — including Nigerian, US, and UK standards.",
        accent: "accent",
      },
      {
        label: "Photographers",
        icon: "Camera",
        description:
          "Fine-tune the composition of raw exports by removing distracting edges before final client delivery, without recompressing the file.",
        accent: "accent",
      },
    ],
  },
};

export default imageCropperTool;
export { imageCropperTool };
